import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
} from 'react'

import { SuggestionProps } from '@tiptap/suggestion'
import { clsx } from 'clsx'

import { debounce as _debounce } from '@bettermode/common/debounce'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { MemberAvatarPlaceholder } from '../../../common/components/index.js'
import { T } from '../../../i18n/components/T.js'
import { Mention } from '../../@types/index.js'
import { MenuList } from '../../components/MenuList.js'

export type MentionListProps = SuggestionProps & {
  search: (variables: { query: string }) => Promise<Mention[]>
  cachedMentions: Mention[]
  items: unknown[]
  command: unknown
}

export const MentionList = forwardRef<unknown, MentionListProps>(
  ({ search, command, query, cachedMentions }, ref) => {
    const refs = useRef([])
    const [items, setItems] = useState<Mention[]>(cachedMentions)
    const [loading, setLoading] = useState<boolean>(false)
    const [fetching, setFetching] = useState<boolean>(false)
    const [selectedIndex, setSelectedIndex] = useState(0)

    const selectItem = index => {
      const item = items?.[index]

      if (item) {
        command({ id: item.id, label: item.title })
      }
    }

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length)
    }

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % items.length)
    }

    const enterHandler = () => {
      selectItem(selectedIndex)
    }

    useEffect(() => setSelectedIndex(0), [items])

    useEffect(() => {
      const el = refs.current[selectedIndex]
      if (el) {
        el.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'start',
        })
      }
    }, [selectedIndex])

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          upHandler()
          return true
        }

        if (event.key === 'ArrowDown') {
          downHandler()
          return true
        }

        if (event.key === 'Enter') {
          enterHandler()
          return true
        }

        return false
      },
    }))

    const debounce = useCallback(
      _debounce((debouncedQuery: string) => {
        const filteredCachedMentions =
          cachedMentions?.filter(c =>
            c.title.toLowerCase().includes(debouncedQuery.toLowerCase()),
          ) || []
        const cachedMap = filteredCachedMentions?.reduce(
          (map, mention) => ({ ...map, [mention.id]: mention }),
          {},
        )
        if (typeof search === 'function') {
          setFetching(true)
          search({
            query: debouncedQuery?.replace(/[_-]/g, ' '),
          })
            .then(items => {
              const reducedResult = items?.filter(m => !cachedMap[m.id]) || []
              setItems([...filteredCachedMentions, ...reducedResult])
            })
            .finally(() => {
              setFetching(false)
              setLoading(false)
            })
        }
      }, 300),
      [],
    )

    useEffect(() => {
      const cachedItems = cachedMentions?.filter(c =>
        c.title.toLowerCase().includes(query.toLowerCase()),
      )
      setLoading(true)
      if (cachedItems) {
        setItems(cachedItems)
      }
      debounce(query)
    }, [query, debounce, cachedMentions])

    if (!items?.length && !query) {
      return null
    }

    if (Array.isArray(items) && !items?.length && !loading && !fetching) {
      return (
        <MenuList>
          <p className="text-center bg-surface text-content flex items-center px-4 py-2 space-s-2">
            <T
              id="Composer.Mention.NotFoundErrorMessage"
              defaultMessage="Couldn’t find any match"
            />
          </p>
        </MenuList>
      )
    }

    return (
      <MenuList>
        {items?.map((item, index) => (
          <span
            ref={el => {
              refs.current[index] = el
            }}
            className={clsx(
              index === selectedIndex
                ? 'bg-surface-subdued text-content'
                : 'bg-surface text-content',
              'flex items-center px-4 py-2 space-s-2',
              'cursor-pointer',
            )}
            key={item.id}
            onClick={() => selectItem(index)}
          >
            <Avatar
              size="xl"
              src={item.icon}
              name={item.title}
              fallback={MemberAvatarPlaceholder}
            />
            <span className="truncate">{item.title}</span>
          </span>
        ))}
        {fetching && (
          <div
            className={clsx(
              'flex justify-center items-center px-4 py-2',
              !items?.length && 'w-28', // min-width when the list is empty
            )}
          >
            <SvgIcon className="animate-spin" name="spinner" />
          </div>
        )}
      </MenuList>
    )
  },
)
